import { Component, computed, signal } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { ModalInput, ModalSection } from "@interfaces/modal";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import { AppActions } from "@state/actions/app.actions";
import { selectCurrentModal } from "@state/selectors/app.selectors";
import { DurationOption } from "src/app/directives/transition/transition.directive";

export enum ModalType {
  CONFIRM = "confirm",
  SUCCESS = "success",
  ERROR = "error",
  INFO = "info",
  DELETION = "deletion",
  INPUT = "input"
}

@Component({
  selector: "app-modal",
  templateUrl: "./modal.component.html",
  styleUrls: ["./modal.component.scss"]
})
export class ModalComponent {
  active = signal(false);
  modalTransitionDuration: DurationOption = 200;
  modal = this.store.selectSignal(selectCurrentModal);

  form = computed(() => {
    const form = new FormGroup({});
    const modal = this.modal();

    const inputs: ModalInput[] = [];

    if (modal?.inputs?.length) {
      inputs.push(...modal.inputs);
    } else if (modal?.input) {
      inputs.push(modal.input);
    }

    inputs.forEach((input) => {
      const control = new FormControl(
        "",
        input.required ? Validators.required : []
      );

      form.addControl(input.fieldName, control);
    });

    return form;
  });

  get isValid() {
    const form = this.form();
    const inputs = this.inputs;

    if (!form.valid) {
      return false;
    }

    return inputs.every((input) => {
      if (!input.isValid) {
        return true;
      }

      const value = form.get(input.fieldName)?.value ?? "";
      return input.isValid(String(value));
    });
  }

  get inputLabel() {
    return this.modal().input?.label ?? "";
  }

  get input() {
    return this.modal().input;
  }

  get inputs(): ModalInput[] {
    const modal = this.modal();

    if (modal?.inputs?.length) {
      return modal.inputs;
    }

    return modal?.input ? [modal.input] : [];
  }

  get formControlName() {
    return this.modal().input?.fieldName ?? "";
  }

  get inputValue() {
    return this.form().get(this.formControlName)?.value;
  }

  /** Modal com três ações (confirmar, cancelar e a saída neutra). */
  get hasNeutral() {
    return !!this.modal()?.neutralText;
  }

  get sections(): ModalSection[] {
    return this.modal()?.sections ?? [];
  }

  get messages() {
    return Array.isArray(this.modal().message)
      ? this.modal().message
      : [this.modal().message];
  }

  changeModal$ = toObservable(this.modal).subscribe((modal) => {
    if (modal) {
      setTimeout(() => {
        this.active.set(true);
      }, this.modalTransitionDuration);
    }
  });

  active$ = toObservable(this.active).subscribe((active) => {
    if (active) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  });

  constructor(private readonly store: Store) {}

  close() {
    this.store.dispatch(
      AppActions.removeModal({
        id: this.modal()?.id
      })
    );
  }

  confirm() {
    this.active.set(false);

    setTimeout(() => {
      if (this.modal().confirmValues) {
        this.modal().confirmValues?.(this.form().getRawValue());
        return;
      }

      this.modal().confirm?.(this.inputValue);
    }, this.modalTransitionDuration);
  }

  cancel() {
    this.active.set(false);

    setTimeout(() => {
      this.modal().cancel?.();
    }, this.modalTransitionDuration);
  }

  /** Fecha o modal sem confirmar nem cancelar (ex.: "Continuar editando"). */
  neutral() {
    this.active.set(false);

    setTimeout(() => {
      this.modal().neutral?.();
    }, this.modalTransitionDuration);
  }
}
